import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, HStack, Flex, Link } from "@chakra-ui/react"
import { Text } from "@/components/ui"
import { useAuth } from "@/contexts"
import { SignInComponent } from "./components"
import { useMsal } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import { loginRequest } from "@/config/msalConfig"
import { authenticateWithMicrosoft } from "@/api/auth"

export const LoginComponent = () => {
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const { instance: msalInstance, accounts, inProgress } = useMsal()

  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const handledRef = useRef(false)

  // index.tsx calls msalInstance.initialize() before React renders, which internally
  // processes the redirect result. By the time this component mounts, handleRedirectPromise()
  // returns null. Instead, watch accounts + inProgress: once inProgress settles to None
  // and an account exists, we know the redirect was processed — acquire the token silently.
  useEffect(() => {
    if (handledRef.current) return
    if (inProgress !== InteractionStatus.None) return
    if (accounts.length === 0) return

    handledRef.current = true
    setIsLoading(true)
    msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: accounts[0],
    }).then(async (result) => {
      try {
        const user = await authenticateWithMicrosoft(result.idToken!)
        await refreshUser(user)
        navigate("/core/pipeline")
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || "Sign-in failed. Please try again."
        setLoginError(msg)
        setIsLoading(false)
        handledRef.current = false
      }
    }).catch((err) => {
      console.error("acquireTokenSilent failed:", err)
      setLoginError("Sign-in failed. Please try again.")
      setIsLoading(false)
      handledRef.current = false
    })
  }, [inProgress, accounts])

  const handleMicrosoftLogin = async () => {
    setIsLoading(true)
    setLoginError(null)
    try {
      await msalInstance.loginRedirect(loginRequest)
      // Page navigates away — nothing below this runs
    } catch (err: any) {
      console.error("Microsoft login error:", err)
      const msg = err?.response?.data?.error || err?.message || "Sign-in failed. Please try again."
      setLoginError(msg)
      setIsLoading(false)
    }
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      minH="100vh"
      position="relative"
      overflow="hidden"
    >
      <Text
        fontSize="xl"
        fontWeight="bold"
        zIndex={1}
        color="white"
        position="absolute"
        top={10}
        left={10}
      >
        ESO Fund Client Portal
      </Text>
      <SignInComponent
        handleMicrosoftLogin={handleMicrosoftLogin}
        isLoading={isLoading}
        loginError={loginError}
      />
      <HStack gap={10} position="absolute" bottom={10} zIndex={1}>
        <Text fontSize="sm" fontWeight="bold" color="darkBlue.100">
          © ESO Fund 2025. All rights reserved.
        </Text>
        <Text as={Link} color="brand.25" fontSize="sm" fontWeight="bold" onClick={() => window.open("https://www.esofund.com/privacy-policy", "_blank")}>
          Privacy Policy
        </Text>
      </HStack>

      <Box
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="75vh"
        bgGradient="linear-gradient(to right, #1F2838, #5856e7)"
        transform="skewY(-5deg)"
        transformOrigin="top left"
        zIndex={0}
      />
    </Flex>
  )
}
